import React from 'react';
import { Info, TriangleAlert } from 'lucide-react';
import { HoverCard, HoverCardContent, HoverCardPortal, HoverCardTrigger } from 'src/shared/components/ui/hover-card';
import { BaselineFallback } from 'src/shared/helpers/baselineFallback';

type TrendExplainerProps = {
    baselineYear: number;
    comparisonYear: number;
    // Replaces the general example, e.g. for values that are already percentages,
    // where a relative change is easy to misread.
    example?: string;
    // Set when the card could not start from the requested baseline, so "since 2016" under
    // "Baseline 2025" reads as missing data rather than a bug.
    fallback?: BaselineFallback;
};

const GENERAL_EXAMPLE = 'Example: a value that changes from 20 to 22 shows +10%.';

const TrendExplainer = ({ baselineYear, comparisonYear, example = GENERAL_EXAMPLE, fallback }: TrendExplainerProps) => (
    <HoverCard openDelay={150} closeDelay={100}>
        <HoverCardTrigger asChild>
            {fallback ? (
                <button type='button' aria-label='This trend uses a different baseline' className='text-orange-100'>
                    <TriangleAlert className='h-3 w-3' />
                </button>
            ) : (
                <button type='button' aria-label='What does this trend mean?' className='text-grey-80 hover:text-primary'>
                    <Info className='h-3 w-3' />
                </button>
            )}
        </HoverCardTrigger>
        <HoverCardPortal>
            <HoverCardContent className='flex flex-col gap-2 w-[280px] p-3 text-xs text-grey-80'>
                {fallback && <p className='text-grey-100'>{fallbackMessage(fallback, baselineYear)}</p>}
                <p>
                    This is the change from {baselineYear} to {comparisonYear}, as a percentage of the {baselineYear} value.
                </p>
                <p>{example}</p>
            </HoverCardContent>
        </HoverCardPortal>
    </HoverCard>
);

const fallbackMessage = ({ requestedYear, reason }: BaselineFallback, baselineYear: number) => {
    if (reason === 'noData') {
        return `There is no data for ${requestedYear}. This trend starts at ${baselineYear}.`;
    } else {
        return `This card shows ${requestedYear}. A trend cannot start at the year it shows. This trend starts at ${baselineYear}.`;
    }
};

export default TrendExplainer;
